/**
 * UIScene - Minimalist Apple-style HUD
 * Clean, responsive design with proper timer mechanics
 */

import Phaser from 'phaser';
import { TOTAL_LEVELS } from '../configs/LevelConfigs';
import { GlassPanel, GlassButton, ModernText } from '../ui/components';
import { UIColors, UITypography, UIAnimation, UISpacing } from '../ui/UIDesignTokens';
import eventBus, { GameEvents } from '../core/EventBus';

export default class UIScene extends Phaser.Scene {
    constructor() {
        super('UIScene');
        this.score = 0;
        this.scoreText = null;
        this.currentLevel = 1;
        this.timerStarted = false;
        this.timerPaused = false;
        this.elapsedTime = 0;
        
        // Settings (loaded from localStorage)
        this.settings = {
            showTimer: true,
            showControls: true
        };
        
        // Event unsubscribers for cleanup
        this.eventUnsubscribers = [];
    }

    init(data) {
        this.currentLevel = data.level || 1;
        this.levelName = data.levelName || 'Level';
        this.totalLevels = data.totalLevels || TOTAL_LEVELS;
        this.wonData = data.won ? data : null;
        this.timerStarted = false;
        this.timerPaused = false;
        this.elapsedTime = 0;
        
        // Load settings
        this.loadSettings();
        
        // Clear previous subscriptions
        this.eventUnsubscribers = [];
    }

    /**
     * Load settings from localStorage
     */
    loadSettings() {
        try {
            const saved = localStorage.getItem('bigblueball_settings');
            if (saved) {
                const parsed = JSON.parse(saved);
                this.settings.showTimer = parsed.showTimer !== false;
                this.settings.showControls = parsed.showControls !== false;
            }
        } catch (e) {
            // Use defaults
        }
    }

    create(data) {
        this.children.removeAll();
        this.subscribeToEvents();

        if (this.wonData || (data && data.won)) {
            // Fade in from white for win screen
            this.cameras.main.fadeIn(400, 255, 255, 255);
            this.showWinScreen(data);
        } else {
            this.setupHUD();
            this.listenToGameEvents();
        }
    }

    /**
     * Subscribe to EventBus events with cleanup tracking
     */
    subscribeToEvents() {
        // Store unsubscribe functions for proper cleanup
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.STAR_COLLECTED, this.onStarCollected, this)
        );
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.PLAYER_DEATH, this.onPlayerDeath, this)
        );
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.TIMER_UPDATE, this.onTimerUpdate, this)
        );
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.LEVEL_COMPLETE, this.onLevelComplete, this)
        );
    }

    /**
     * Handle star collection - update score display
     */
    onStarCollected(data) {
        // Update score
        this.score = data.count || this.score + 1;
        if (this.scoreText) {
            this.scoreText.setText(this.score.toString());
            this.tweens.add({
                targets: this.scoreText,
                scaleX: 1.3,
                scaleY: 1.3,
                duration: 100,
                yoyo: true
            });
        }
        
        // Animate star icon
        if (this.starIcon) {
            this.tweens.add({
                targets: this.starIcon,
                scaleX: 1.4,
                scaleY: 1.4,
                duration: 100,
                yoyo: true
            });
        }
    }

    onPlayerDeath(data) {
        if (this.hudContainer) {
            this.tweens.add({
                targets: this.hudContainer,
                alpha: 0.3,
                duration: 100,
                yoyo: true,
                repeat: 2
            });
        }
    }

    setupHUD() {
        const { width, height } = this.cameras.main;
        this.score = 0;

        this.hudContainer = this.add.container(0, 0);

        // === TOP LEFT: Level Badge ===
        this.createLevelIndicator(20, 20);
        
        // === BELOW LEVEL: Star Counter (properly aligned) ===
        this.createScoreIndicator(20, 58);

        // === TOP RIGHT: Timer (if enabled in settings) ===
        if (this.settings.showTimer) {
            this.createTimer(width - 20, 20);
        }

        // === BOTTOM: Controls hint (if enabled in settings) ===
        if (this.settings.showControls) {
            this.createControlsHint(width / 2, height - 20);
        }

        // Animate HUD in
        this.hudContainer.setAlpha(0);
        this.hudContainer.y = -20;
        this.tweens.add({
            targets: this.hudContainer,
            alpha: 1,
            y: 0,
            duration: 500,
            ease: 'Power2.easeOut'
        });
    }

    createLevelIndicator(x, y) {
        const pillWidth = 110;
        const pillHeight = 30;
        
        const levelPill = this.add.graphics();
        levelPill.fillStyle(UIColors.primary, 0.2);
        levelPill.fillRoundedRect(x, y, pillWidth, pillHeight, pillHeight / 2);
        levelPill.lineStyle(1.5, UIColors.primary, 0.4);
        levelPill.strokeRoundedRect(x, y, pillWidth, pillHeight, pillHeight / 2);
        this.hudContainer.add(levelPill);
        
        const levelText = this.add.text(x + pillWidth / 2, y + pillHeight / 2, `LEVEL ${this.currentLevel}`, {
            fontSize: '14px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightSemibold,
            fill: '#5AC8FA'
        }).setOrigin(0.5);
        this.hudContainer.add(levelText);
    }

    createScoreIndicator(x, y) {
        // Container for proper alignment
        const scoreContainer = this.add.container(x, y);
        
        // Star icon - properly sized and positioned
        this.starIcon = this.add.star(14, 12, 5, 6, 14, 0xFFD700);
        this.starIcon.setStrokeStyle(1.5, 0xFFA500);
        scoreContainer.add(this.starIcon);
        
        // Score text - aligned with star center
        this.scoreText = this.add.text(32, 12, '0', {
            fontSize: '20px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#FFD700'
        }).setOrigin(0, 0.5);
        scoreContainer.add(this.scoreText);
        
        this.hudContainer.add(scoreContainer);
        
        // Subtle glow on star
        this.tweens.add({
            targets: this.starIcon,
            alpha: 0.7,
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    createTimer(x, y) {
        // Timer container (right-aligned)
        const timerContainer = this.add.container(x, y);
        
        // Timer background pill
        const pillWidth = 120;
        const pillHeight = 30;
        
        const timerBg = this.add.graphics();
        timerBg.fillStyle(0x1C1C1E, 0.6);
        timerBg.fillRoundedRect(-pillWidth, 0, pillWidth, pillHeight, pillHeight / 2);
        timerContainer.add(timerBg);
        
        // Timer icon
        const timerIcon = this.add.text(-pillWidth + 12, pillHeight / 2, '⏱', {
            fontSize: '14px',
            fill: '#8E8E93'
        }).setOrigin(0, 0.5);
        timerContainer.add(timerIcon);
        
        // Timer text (monospace for consistency)
        this.timerText = this.add.text(-pillWidth + 35, pillHeight / 2, '00:00.00', {
            fontSize: '16px',
            fontFamily: UITypography.fontFamilyMono,
            fontStyle: UITypography.weightMedium,
            fill: '#ffffff'
        }).setOrigin(0, 0.5);
        timerContainer.add(this.timerText);
        
        // "START MOVING" hint that fades when timer starts
        this.timerHint = this.add.text(-pillWidth / 2, pillHeight + 8, 'Move to start timer', {
            fontSize: '10px',
            fontFamily: UITypography.fontFamily,
            fill: '#636366'
        }).setOrigin(0.5, 0);
        timerContainer.add(this.timerHint);
        
        this.hudContainer.add(timerContainer);
    }

    createControlsHint(x, y) {
        const controls = this.add.text(x, y, '← → Move    ↑ Jump    ↓ Pull    Shift Dash    R Restart    M Menu', {
            fontSize: '11px',
            fontFamily: UITypography.fontFamily,
            fill: '#48484A',
            backgroundColor: '#1C1C1E80',
            padding: { x: 14, y: 8 }
        }).setOrigin(0.5, 1);
        
        // Fade out after 6 seconds
        this.tweens.add({
            targets: controls,
            alpha: 0,
            delay: 6000,
            duration: 1000
        });
    }

    /**
     * Listen to game events via EventBus (unified event system)
     * No longer uses Phaser scene events for cross-scene communication
     */
    listenToGameEvents() {
        // All events now handled via EventBus in subscribeToEvents()
        // This method kept for backwards compatibility
    }

    /**
     * Handle timer update from EventBus
     */
    onTimerUpdate(data) {
        if (data && data.started && !this.timerStarted && !this.timerPaused) {
            this.startTimer();
        }
    }

    /**
     * Handle level complete from EventBus
     */
    onLevelComplete(data) {
        this.stopTimer();
    }

    /**
     * Cleanup all event subscriptions
     */
    cleanup() {
        // Unsubscribe from all EventBus events
        this.eventUnsubscribers.forEach(unsub => {
            if (typeof unsub === 'function') {
                unsub();
            }
        });
        this.eventUnsubscribers = [];
        
        // Stop timer if running
        if (this.timerEvent) {
            this.timerEvent.remove();
        }
    }

    /**
     * Scene shutdown handler
     */
    shutdown() {
        this.cleanup();
    }

    startTimer() {
        this.timerStarted = true;
        this.startTime = this.time.now;
        
        // Hide hint
        if (this.timerHint) {
            this.tweens.add({
                targets: this.timerHint,
                alpha: 0,
                duration: 300
            });
        }
        
        // Start timer update loop
        this.timerEvent = this.time.addEvent({
            delay: 10,
            loop: true,
            callback: () => {
                if (!this.timerPaused) {
                    this.elapsedTime = this.time.now - this.startTime;
                    if (this.timerText) {
                        this.timerText.setText(this.formatTime(this.elapsedTime));
                    }
                }
            }
        });
        
        // Pulse animation on timer
        this.tweens.add({
            targets: this.timerText,
            scaleX: 1.1,
            scaleY: 1.1,
            duration: 150,
            yoyo: true
        });
    }

    stopTimer() {
        this.timerPaused = true;
        if (this.timerEvent) {
            this.timerEvent.remove();
        }
    }

    getElapsedTime() {
        return this.elapsedTime;
    }

    formatTime(ms) {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        const centiseconds = Math.floor((ms % 1000) / 10);

        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
    }

    // ==================== WIN SCREEN ====================

    showWinScreen(data) {
        const { width, height } = this.cameras.main;

        const starsCollected = data.starsCollected || this.score;
        const totalStars = data.totalStars || 3;
        const currentLevel = data.level || this.currentLevel;
        const levelName = data.levelName || `Level ${currentLevel}`;
        const timeTaken = data.timeTaken || this.elapsedTime || 0;
        const isLastLevel = currentLevel >= TOTAL_LEVELS;

        // Dark overlay with blur effect simulation
        const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0);
        this.tweens.add({
            targets: overlay,
            fillAlpha: 0.92,
            duration: 600,
            ease: 'Power2.easeOut'
        });

        this.time.delayedCall(400, () => {
            this.createWinPanel(width, height, {
                starsCollected,
                totalStars,
                currentLevel,
                levelName,
                timeTaken,
                isLastLevel
            });
        });

        // Keyboard shortcuts
        this.input.keyboard.once('keydown-R', () => {
            this.restartLevel(currentLevel);
        });

        if (!isLastLevel) {
            this.input.keyboard.once('keydown-ENTER', () => {
                this.nextLevel(currentLevel);
            });
        }

        this.input.keyboard.once('keydown-M', () => {
            this.goToMenu();
        });
    }

    createWinPanel(width, height, data) {
        const panelWidth = 380;
        const panelHeight = 400;
        const panelX = width / 2 - panelWidth / 2;
        const panelY = height / 2 - panelHeight / 2 - 25;

        // Main panel with glassmorphism
        const panel = this.add.graphics();
        panel.fillStyle(0x1C1C1E, 0.95);
        panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 20);
        
        // Subtle gradient overlay
        panel.fillGradientStyle(0x2C2C2E, 0x2C2C2E, 0x1C1C1E, 0x1C1C1E, 0.5);
        panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight / 2, { tl: 20, tr: 20, bl: 0, br: 0 });
        
        // Border
        panel.lineStyle(1.5, 0xffffff, 0.1);
        panel.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 20);
        
        // Animate panel in
        panel.setAlpha(0);
        panel.setScale(0.9);
        this.tweens.add({
            targets: panel,
            alpha: 1,
            scaleX: 1,
            scaleY: 1,
            duration: 500,
            ease: 'Back.easeOut'
        });

        // === SUCCESS BADGE ===
        const badgeY = panelY + 35;
        this.createSuccessBadge(width / 2, badgeY, data.isLastLevel);

        // === LEVEL NAME ===
        const levelNameY = badgeY + 50;
        const levelTitle = this.add.text(width / 2, levelNameY, data.levelName.toUpperCase(), {
            fontSize: '22px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: levelTitle,
            alpha: 1,
            y: levelNameY,
            delay: 300,
            duration: 400
        });

        // Subtitle
        const subtitle = this.add.text(width / 2, levelNameY + 24, 'COMPLETED', {
            fontSize: '11px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#34C759',
            letterSpacing: 2
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: subtitle,
            alpha: 1,
            delay: 400,
            duration: 300
        });

        // === STATS CARDS ===
        const statsY = levelNameY + 58;
        this.createStatsCards(width / 2, statsY, panelWidth, data);

        // === BUTTONS (Vertical Layout) ===
        const buttonsY = statsY + 75;
        this.createWinButtons(width / 2, buttonsY, panelWidth, data);

        // === KEYBOARD HINTS (below panel, in black area) ===
        const hintsY = panelY + panelHeight + 20;
        this.createKeyboardHints(width / 2, hintsY, data.isLastLevel);
    }

    createSuccessBadge(x, y, isLastLevel) {
        // Glow effect
        const glow = this.add.circle(x, y, 28, isLastLevel ? 0xFFD700 : 0x34C759, 0.15);
        glow.setAlpha(0);
        
        this.tweens.add({
            targets: glow,
            alpha: 1,
            scale: 1.3,
            delay: 200,
            duration: 500,
            yoyo: true,
            repeat: -1
        });

        // Badge circle
        const badge = this.add.circle(x, y, 22, isLastLevel ? 0xFFD700 : 0x34C759, 0.2);
        badge.setStrokeStyle(2, isLastLevel ? 0xFFD700 : 0x34C759);
        badge.setAlpha(0);
        badge.setScale(0);
        
        this.tweens.add({
            targets: badge,
            alpha: 1,
            scaleX: 1,
            scaleY: 1,
            delay: 200,
            duration: 400,
            ease: 'Back.easeOut'
        });

        // Checkmark or trophy
        const icon = this.add.text(x, y, isLastLevel ? '🏆' : '✓', {
            fontSize: isLastLevel ? '20px' : '24px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: isLastLevel ? '#FFD700' : '#34C759'
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: icon,
            alpha: 1,
            delay: 400,
            duration: 300
        });
    }

    createStarsDisplay(x, y, collected, total) {
        const starSpacing = 55;
        const startX = x - ((total - 1) * starSpacing) / 2;
        
        for (let i = 0; i < total; i++) {
            const starX = startX + i * starSpacing;
            const isCollected = i < collected;
            
            // Star glow
            if (isCollected) {
                const glow = this.add.circle(starX, y, 28, 0xFFD700, 0.2);
                glow.setAlpha(0);
                this.tweens.add({
                    targets: glow,
                    alpha: 1,
                    scale: 1.2,
                    delay: 600 + i * 150,
                    duration: 300
                });
            }
            
            // Star
            const star = this.add.star(starX, y, 5, 12, 26, isCollected ? 0xFFD700 : 0x3A3A3C);
            if (isCollected) {
                star.setStrokeStyle(2, 0xFFA500);
            } else {
                star.setStrokeStyle(1, 0x48484A);
            }
            star.setAlpha(0);
            star.setScale(0);
            
            this.tweens.add({
                targets: star,
                alpha: 1,
                scaleX: 1,
                scaleY: 1,
                delay: 600 + i * 150,
                duration: 400,
                ease: 'Back.easeOut',
                onComplete: () => {
                    if (isCollected) {
                        this.tweens.add({
                            targets: star,
                            angle: 360,
                            duration: 3000,
                            repeat: -1
                        });
                    }
                }
            });
        }
    }

    createStatsCards(x, y, panelWidth, data) {
        const cardWidth = Math.min(140, (panelWidth - 50) / 2);
        const cardHeight = 52;
        const cardSpacing = 12;
        
        // Stars card
        const starsCardX = x - cardWidth - cardSpacing / 2;
        this.createStatCard(starsCardX, y, cardWidth, cardHeight, {
            icon: '★',
            value: `${data.starsCollected}/${data.totalStars}`,
            label: 'STARS',
            delay: 600,
            iconColor: '#FFD700'
        });

        // Time card
        const timeCardX = x + cardSpacing / 2;
        this.createStatCard(timeCardX, y, cardWidth, cardHeight, {
            icon: '⏱',
            value: this.formatTime(data.timeTaken),
            label: 'TIME',
            delay: 700,
            iconColor: '#8E8E93'
        });
    }

    createStatCard(x, y, width, height, config) {
        const card = this.add.graphics();
        card.fillStyle(0x2C2C2E, 0.8);
        card.fillRoundedRect(x, y, width, height, 10);
        card.lineStyle(1, 0xffffff, 0.08);
        card.strokeRoundedRect(x, y, width, height, 10);
        card.setAlpha(0);
        
        this.tweens.add({
            targets: card,
            alpha: 1,
            delay: config.delay,
            duration: 300
        });

        // Icon
        const icon = this.add.text(x + 12, y + height / 2, config.icon, {
            fontSize: '18px',
            fontFamily: UITypography.fontFamily,
            fill: config.iconColor || '#ffffff'
        }).setOrigin(0, 0.5).setAlpha(0);

        // Value
        const value = this.add.text(x + 38, y + height / 2 - 5, config.value, {
            fontSize: '16px',
            fontFamily: UITypography.fontFamilyMono,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0, 0.5).setAlpha(0);

        // Label
        const label = this.add.text(x + 38, y + height / 2 + 11, config.label, {
            fontSize: '9px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#8E8E93',
            letterSpacing: 1
        }).setOrigin(0, 0.5).setAlpha(0);

        this.tweens.add({
            targets: [icon, value, label],
            alpha: 1,
            delay: config.delay + 100,
            duration: 300
        });
    }

    createWinButtons(x, y, panelWidth, data) {
        // Vertical layout with compact buttons
        const buttonWidth = Math.min(220, panelWidth - 50);
        const buttonHeight = 38;
        const buttonSpacing = 10;
        let idx = 0;

        // Next Level (if not last) - Green prominent
        if (!data.isLastLevel) {
            const nextBtn = new GlassButton(this, x, y + idx * (buttonHeight + buttonSpacing), 'NEXT LEVEL →', {
                width: buttonWidth,
                height: buttonHeight,
                bgColor: 0x34C759,
                hoverColor: 0x30D158,
                fontSize: '13px',
                onClick: () => this.nextLevel(data.currentLevel)
            });
            nextBtn.animateIn(900);
            idx++;
        }

        // Restart - Gray secondary
        const restartBtn = new GlassButton(this, x, y + idx * (buttonHeight + buttonSpacing), 'PLAY AGAIN', {
            width: buttonWidth,
            height: buttonHeight,
            bgColor: UIColors.gray700,
            bgAlpha: 0.8,
            hoverColor: UIColors.gray600,
            fontSize: '13px',
            onClick: () => this.restartLevel(data.currentLevel)
        });
        restartBtn.animateIn(950);
        idx++;

        // Menu - Blue accent
        const menuBtn = new GlassButton(this, x, y + idx * (buttonHeight + buttonSpacing), 'MAIN MENU', {
            width: buttonWidth,
            height: buttonHeight,
            bgColor: UIColors.primary,
            bgAlpha: 0.8,
            hoverColor: UIColors.primaryLight,
            fontSize: '13px',
            onClick: () => this.goToMenu()
        });
        menuBtn.animateIn(1000);
    }

    createKeyboardHints(x, y, isLastLevel) {
        const hints = isLastLevel 
            ? '[R] Restart   [M] Menu' 
            : '[ENTER] Next   [R] Restart   [M] Menu';
        
        const hintsText = this.add.text(x, y, hints, {
            fontSize: '12px',
            fontFamily: UITypography.fontFamilyMono,
            fill: '#ffffff',
            letterSpacing: 1
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: hintsText,
            alpha: 0.7,
            delay: 1200,
            duration: 400
        });
    }

    // Navigation methods
    nextLevel(currentLevel) {
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.time.delayedCall(400, () => {
            this.scene.stop('UIScene');
            this.scene.start('GameScene', { level: currentLevel + 1 });
        });
    }

    restartLevel(currentLevel) {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.time.delayedCall(300, () => {
            this.scene.stop('UIScene');
            this.scene.start('GameScene', { level: currentLevel });
        });
    }

    goToMenu() {
        this.cameras.main.fadeOut(400, 0, 0, 0);
        this.time.delayedCall(400, () => {
            this.scene.stop('UIScene');
            this.scene.start('MenuScene');
        });
    }

    shutdown() {
        eventBus.off(GameEvents.STAR_COLLECTED, this.onStarCollected, this);
        eventBus.off(GameEvents.PLAYER_DEATH, this.onPlayerDeath, this);
        if (this.timerEvent) {
            this.timerEvent.remove();
        }
    }
}
